import { Component, ChangeDetectionStrategy, ElementRef, HostListener, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { ProfileDropdownComponent } from '../profile-dropdown/profile-dropdown.component';

export interface NavLink {
  label: string;
  path: string;
}

/** A top-level trigger that collapses several related routes into one dropdown. */
export interface NavMenu {
  /** Identifies which menu is open; only one is at a time. */
  key: string;
  label: string;
  /** URL prefixes that keep the trigger highlighted while a child route is active. */
  prefixes: string[];
  links: NavLink[];
}

@Component({
  selector: 'hp-navbar',
  standalone: true,
  imports: [RouterModule, NgTemplateOutlet, ProfileDropdownComponent],
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  private router = inject(Router);
  private el = inject(ElementRef);

  readonly navLinks: NavLink[] = [{ label: 'Dashboard', path: '/dashboard' }];
  readonly trailingNavLinks: NavLink[] = [{ label: 'Settlement', path: '/settlement' }];

  readonly ordersMenu: NavMenu = {
    key: 'orders',
    label: 'Orders',
    prefixes: ['/orders-partner'],
    links: [
      { label: 'Active Orders', path: '/orders-partner/active' },
      { label: 'Completed Orders', path: '/orders-partner/completed' },
      { label: 'Returned Orders', path: '/orders-partner/returned' },
      { label: 'Rejected/Cancelled', path: '/orders-partner/closed' },
    ],
  };

  /**
   * The product screens sit on unrelated top-level paths rather than under a
   * shared prefix, so the trigger's highlight is driven by the links' own paths.
   */
  readonly productsMenu: NavMenu = {
    key: 'products',
    label: 'Products',
    prefixes: ['/top-products', '/bounce-products', '/product-mapping', '/product-pricing'],
    links: [
      { label: 'Top Selling Products', path: '/top-products' },
      { label: 'Bounced Products', path: '/bounce-products' },
      { label: 'Product Mapping', path: '/product-mapping' },
      { label: 'Product Pricing', path: '/product-pricing' },
    ],
  };

  /** Key of the menu opened by tap/click, or null. Hover opening is pure CSS. */
  readonly openMenu = signal<string | null>(null);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /** Highlights a trigger while any of its submenu routes is active. */
  isMenuActive(menu: NavMenu): boolean {
    const url = this.currentUrl();
    return menu.prefixes.some((prefix) => url.startsWith(prefix));
  }

  toggleMenu(key: string, event: Event): void {
    event.stopPropagation();
    this.openMenu.update((open) => (open === key ? null : key));
  }

  closeMenu(): void {
    this.openMenu.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.closeMenu();
    }
  }
}
